/**
 * Hold focus inside an overlay, and give it back on the way out.
 *
 * Extracted from `Dialog` so the navigation drawer can have exactly the same
 * behaviour rather than a second, subtly different implementation. The parts
 * people skip are the parts that matter, and there are four of them:
 *
 * *   **Focus goes in.** The first field, or the panel itself when there is
 *     nothing focusable — otherwise a keyboard user opens a drawer and is still
 *     standing on the button behind it.
 * *   **Focus stays in.** Tab wraps at both ends. Without it, Tab walks out of
 *     the overlay and into a page that is still fully interactive to a keyboard
 *     and completely hidden behind a backdrop.
 * *   **Escape closes**, and the handler runs on the capture phase so a nested
 *     overlay closes itself rather than its parent.
 * *   **Focus comes back** to whatever opened it, so the keyboard does not jump
 *     to the top of the page afterwards.
 *
 * The page behind is also stopped from scrolling. On a touchscreen a stray drag
 * scrolling the list underneath an open drawer is disorienting, and on a POS it
 * loses somebody's place in a product grid.
 */

import { useEffect, type RefObject } from "react";

const FOCUSABLE =
  "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), " +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface FocusTrapOptions {
  /** Whether the overlay is on screen. Nothing happens while this is false. */
  active: boolean;
  /** The element to hold focus inside. */
  panel: RefObject<HTMLElement | null>;
  /** Called on Escape and expected to unmount or hide the overlay. */
  onClose: () => void;
  /**
   * Lock the page behind. On by default.
   *
   * Off for an overlay that only covers part of the screen at some sizes — a
   * navigation drawer is a drawer on a phone and a static rail on a desktop, and
   * locking the body at desktop width would freeze a page nothing is covering.
   */
  lockScroll?: boolean;
}

export function useFocusTrap({
  active,
  panel,
  onClose,
  lockScroll = true,
}: FocusTrapOptions) {
  useEffect(() => {
    if (!active) return;

    const opener = document.activeElement as HTMLElement | null;

    const previousOverflow = document.body.style.overflow;
    if (lockScroll) document.body.style.overflow = "hidden";

    // Only elements that are actually rendered.
    //
    // `querySelectorAll` happily returns a `display: none` control, and
    // `.focus()` on one silently does nothing — so an overlay whose first
    // focusable element is hidden at the current breakpoint opens with focus
    // still on the button behind it, and Tab then walks into the page behind.
    // The navigation drawer hit exactly this: its close button is `lg:hidden`,
    // so at desktop width the drawer opened focusing nothing at all.
    //
    // `checkVisibility` when the engine has it, and **assume visible** when it
    // does not. jsdom lays nothing out — every element there reports no boxes —
    // so a geometric test would filter out every control in every test and
    // quietly move the assertions off the real behaviour.
    const isRendered = (element: HTMLElement) =>
      typeof element.checkVisibility === "function"
        ? element.checkVisibility()
        : true;

    const focusable = () =>
      Array.from(
        panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
      ).filter(isRendered);

    // Focus now, not on the next frame.
    //
    // Effects run after React has committed to the DOM, so the panel and its
    // children are already there — `.focus()` needs an element, not a painted
    // one. The obvious `requestAnimationFrame` wrapper is worse than it looks:
    // a browser is free not to run one for a tab it is not compositing, and
    // then the overlay opens with focus left on the page behind it. That is
    // rare in front of a person and routine under automation, which is exactly
    // the sort of difference that gets shipped.
    //
    // The frame is kept only as a second attempt, for a panel whose contents
    // arrive a tick late — a portal rendering into a node created in the same
    // commit, say.
    const focusFirst = () => (focusable()[0] ?? panel.current)?.focus();

    focusFirst();
    const frame =
      panel.current && panel.current.contains(document.activeElement)
        ? 0
        : window.requestAnimationFrame(focusFirst);

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      const items = focusable();
      if (items.length === 0) return;

      const first = items[0]!;
      const last = items[items.length - 1]!;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown, true);
      if (lockScroll) document.body.style.overflow = previousOverflow;
      opener?.focus();
    };
  }, [active, panel, onClose, lockScroll]);
}
